import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { Icon } from '../components/icons';
import Spinner from '../components/Spinner';
import { exportMcpGeneratedSpec } from '../utils/exportUtils';
import type { AutomationJob, AutomationJobStatus } from '../types';

const STATUS_LABEL: Record<AutomationJobStatus, string> = {
  queued: 'Queued',
  generating: 'Generating',
  healing: 'Healing',
  passed: 'Passed',
  failed: 'Failed',
};

const IN_FLIGHT: Set<AutomationJobStatus> = new Set(['queued', 'generating', 'healing']);
const POLL_MS = 3000;

export default function McpCodegenPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate   = useNavigate();
  const { instance } = useMsal();
  const cfg        = useRuntimeConfig();
  const api        = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);

  const [job, setJob]     = useState<AutomationJob | null>(null);
  const [error, setError] = useState('');
  const statusRef = useRef<AutomationJobStatus>('queued');
  const logRef    = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (job) statusRef.current = job.status;
  }, [job]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    async function poll() {
      try {
        const j = await api.getAutomationJob(jobId!);
        if (!cancelled) setJob(j);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load job.');
      }
    }

    poll();
    const t = setInterval(() => {
      if (!IN_FLIGHT.has(statusRef.current)) { clearInterval(t); return; }
      poll();
    }, POLL_MS);

    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [job?.log]);

  function handleDownload() {
    if (!job?.finalCode) return;
    exportMcpGeneratedSpec(job.finalCode, job.caseTitle);
  }

  if (error) return <div className="page-error">Failed to load: {error}</div>;
  if (!job)  return <div className="page-loading"><span className="spinner" />Loading…</div>;

  const inFlight = IN_FLIGHT.has(job.status);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/web-automation')}>
          <Icon name="arrow-left" size={13} /> Web Automation
        </button>
        <h1 className="page-title">Generate Code with MCP</h1>
        <p className="page-subtitle">{job.caseTitle}</p>
      </div>

      <div className="stack-gap-sm">
        <span className={`tc-status-badge mcp-status-${job.status}`}>
          {inFlight && <Spinner size="sm" />}{' '}
          {STATUS_LABEL[job.status] ?? job.status}
          {job.status === 'healing' && ` (attempt ${job.attempt})`}
        </span>

        {job.log && (
          <pre ref={logRef} className="playwright-preview-code" style={{ maxHeight: 240 }}>
            {job.log}
          </pre>
        )}

        {job.status === 'failed' && job.errorMessage && (
          <p style={{ color: 'var(--danger)' }}>{job.errorMessage}</p>
        )}

        {job.finalCode && (
          <>
            <pre className="playwright-preview-code">{job.finalCode}</pre>
            <button className="btn btn-primary" onClick={handleDownload}>
              <Icon name="download" size={14} /> Download .spec.ts
            </button>
          </>
        )}
      </div>
    </div>
  );
}
